// The workspace's connected accounts, loaded once and shared.
//
// Three things ask for this list on a single screen — the favourites rail, the
// top bar, and the composer's target picker — and before this hook each of them
// issued its own query on every navigation. The list changes only on the
// Connections screen, so it is cached per workspace and invalidated explicitly
// from there rather than re-read on a timer.
//
// Deliberately not in `WorkspaceContext`: that answers "who am I and what may I
// do", which every private route needs before it can render anything. This is a
// screen's data, and a failure to load it must never look like a failure to
// load the session.

import { useCallback, useEffect, useState } from "react";
import { listConnections } from "./api";
import type { Connection } from "./types";

const cache = new Map<string, Connection[]>();
const listeners = new Set<(workspaceId: string) => void>();

/** Drop the cached list so the next reader re-reads it. Call after connect,
 *  disconnect, or anything else that changes what is on the Connections page. */
export function forgetConnections(workspaceId?: string) {
  if (workspaceId) cache.delete(workspaceId);
  else cache.clear();
  for (const notify of listeners) notify(workspaceId ?? "");
}

export interface ConnectionsState {
  connections: Connection[];
  /** False until the first answer — cached or fresh — has arrived. */
  ready: boolean;
  error: string | null;
  reload: () => Promise<void>;
}

export function useConnections(workspaceId: string | null): ConnectionsState {
  const [connections, setConnections] = useState<Connection[]>(
    () => (workspaceId ? cache.get(workspaceId) ?? [] : []),
  );
  const [ready, setReady] = useState(() => !!workspaceId && cache.has(workspaceId));
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async (force: boolean) => {
    if (!workspaceId) {
      setConnections([]);
      setReady(false);
      return;
    }
    const hit = cache.get(workspaceId);
    if (hit && !force) {
      setConnections(hit);
      setReady(true);
      return;
    }
    try {
      const rows = await listConnections(workspaceId);
      cache.set(workspaceId, rows);
      setConnections(rows);
      setError(null);
    } catch (e: any) {
      // The rail and the bar draw nothing rather than an error: an account list
      // that failed to load is not something the reader can act on from here.
      // The composer, which can, reads `error` and says so.
      setError(e?.message ?? String(e));
    } finally {
      setReady(true);
    }
  }, [workspaceId]);

  useEffect(() => { void load(false); }, [load]);

  // Another part of the app dropped the cache — pick the change up without a
  // navigation, so disconnecting an account empties the rail immediately.
  useEffect(() => {
    const notify = (id: string) => {
      if (!workspaceId) return;
      if (id && id !== workspaceId) return;
      void load(true);
    };
    listeners.add(notify);
    return () => { listeners.delete(notify); };
  }, [workspaceId, load]);

  return { connections, ready, error, reload: () => load(true) };
}
